import { create } from 'zustand';
import { supabase } from 'common/src/supabase';

export type LabelCategory = 'PREMIUM' | 'ESTANDAR' | 'REGULAR';

export interface ExporterLabelRecord {
  id: number;
  exporter_id: number;
  species_id: number;
  season: string;
  label: string;
  category: LabelCategory;
  created_at: string;
}

type LabelContext = {
  exporterId: number;
  speciesId: number;
  season: string;
};

type ExporterLabelsStore = {
  labels: ExporterLabelRecord[];
  loading: boolean;
  lastContext?: LabelContext;
  fetchLabels: (
    exporterId: number,
    speciesId: number,
    season: string,
    force?: boolean
  ) => Promise<void>;
  addLabel: (
    exporterId: number,
    speciesId: number,
    season: string,
    label: string,
    category: LabelCategory
  ) => Promise<ExporterLabelRecord>;
  updateLabel: (
    id: number,
    label: string,
    category: LabelCategory
  ) => Promise<ExporterLabelRecord>;
  deleteLabel: (id: number) => Promise<void>;
  reset: () => void;
};

const sortByLabel = (items: ExporterLabelRecord[]) =>
  [...items].sort((a, b) => a.label.localeCompare(b.label));

const useExporterLabelsStore = create<ExporterLabelsStore>((set, get) => ({
  labels: [],
  loading: false,
  lastContext: undefined,
  fetchLabels: async (exporterId, speciesId, season, force = false) => {
    const last = get().lastContext;
    if (
      !force &&
      last &&
      last.exporterId === exporterId &&
      last.speciesId === speciesId &&
      last.season === season
    ) {
      return;
    }
    set({ loading: true });
    try {
      const { data, error } = await supabase
        .from('exporter_labels')
        .select('*')
        .eq('exporter_id', exporterId)
        .eq('species_id', speciesId)
        .eq('season', season)
        .order('label', { ascending: true });
      if (error) throw error;
      set({
        labels: sortByLabel((data as ExporterLabelRecord[]) || []),
        lastContext: { exporterId, speciesId, season },
      });
    } finally {
      set({ loading: false });
    }
  },
  addLabel: async (exporterId, speciesId, season, label, category) => {
    const { data, error } = await supabase
      .from('exporter_labels')
      .insert({
        exporter_id: exporterId,
        species_id: speciesId,
        season,
        label,
        category,
      })
      .select('*')
      .single();
    if (error) throw error;
    const created = data as ExporterLabelRecord;
    set((state) => {
      const last = state.lastContext;
      const matchesContext =
        last &&
        last.exporterId === exporterId &&
        last.speciesId === speciesId &&
        last.season === season;
      if (!matchesContext) {
        return state;
      }
      return {
        labels: sortByLabel([...state.labels, created]),
      };
    });
    return created;
  },
  updateLabel: async (id, label, category) => {
    const { data, error } = await supabase
      .from('exporter_labels')
      .update({ label, category })
      .eq('id', id)
      .select('*')
      .single();
    if (error) throw error;
    const updated = data as ExporterLabelRecord;
    set((state) => ({
      labels: sortByLabel(
        state.labels.map((item) => (item.id === id ? updated : item))
      ),
    }));
    return updated;
  },
  deleteLabel: async (id) => {
    const { error } = await supabase
      .from('exporter_labels')
      .delete()
      .eq('id', id);
    if (error) throw error;
    set((state) => ({
      labels: state.labels.filter((item) => item.id !== id),
    }));
  },
  reset: () => set({ labels: [], loading: false, lastContext: undefined }),
}));

export default useExporterLabelsStore;
